// The chart spec the LLM emits inside a ```chart fenced block. Deliberately small and
// library-agnostic so the model never writes Nivo/library config directly — ChartRenderer
// maps this to Nivo, and the library can be swapped without touching the agent.
import { ValueFormat } from './chartPalette'

export type ChartType = 'bar' | 'line' | 'area' | 'pie' | 'donut'

export interface ChartSeries {
  key: string // column in `data` holding this series' values
  name?: string // display label (defaults to key)
  color?: string // palette colour name (defaults to auto-assigned)
}

export interface ChartSpec {
  type: ChartType
  title?: string
  data: Array<Record<string, unknown>>
  x?: string // category/label column (bar/line/area, and label for pie/donut)
  series?: ChartSeries[] // value columns; pie/donut use the first
  format?: ValueFormat
  stacked?: boolean // bar/area
  height?: number
  // Presentation controls the LLM can set:
  orientation?: 'vertical' | 'horizontal' // bar only
  xLabelRotation?: number // degrees, e.g. -35 for crowded category labels
  valueFormat?: 'full' | 'compact' // compact => £165k instead of £165,340.00
  maxTicks?: number // cap value-axis tick count
  // Raw Nivo props merged over our defaults — full control of the library (colours, axes,
  // margins, curve, legends, …). Cannot override width/height/data.
  nivo?: Record<string, unknown>
}

const TYPES: ChartType[] = ['bar', 'line', 'area', 'pie', 'donut']

export interface ParseResult {
  spec?: ChartSpec
  error?: string
}

/** Parse + validate the fenced block body. Returns {} (no spec, no error) while the JSON
 *  is still incomplete (streaming), so the caller can show a skeleton rather than an error. */
export const parseChartSpec = (raw: string): ParseResult => {
  const trimmed = raw.trim()
  if (!trimmed) return {}

  let parsed: unknown
  try {
    parsed = JSON.parse(trimmed)
  } catch {
    // Likely still streaming (unterminated JSON) — not an error yet.
    return {}
  }

  if (typeof parsed !== 'object' || parsed === null) {
    return { error: 'Chart spec must be a JSON object.' }
  }
  const spec = parsed as Partial<ChartSpec>

  if (!spec.type || !TYPES.includes(spec.type)) {
    return { error: `Unsupported chart type. Use one of: ${TYPES.join(', ')}.` }
  }
  if (!Array.isArray(spec.data) || spec.data.length === 0) {
    return { error: 'Chart spec needs a non-empty "data" array.' }
  }

  // Default series for pie/donut and single-series charts: infer from numeric columns.
  if (!spec.series || spec.series.length === 0) {
    const sample = spec.data[0]
    const numericKeys = Object.keys(sample).filter(
      k => k !== spec.x && typeof sample[k] === 'number'
    )
    if (numericKeys.length === 0) {
      return { error: 'Chart spec needs "series" or numeric columns in "data".' }
    }
    spec.series = numericKeys.map(key => ({ key }))
  }

  return { spec: spec as ChartSpec }
}
